import type { ParseKeys } from 'i18next';

import type { StrategyWizardSessionData } from '@/types/generated';
import { WizardStep } from '@/types/generated/enums';

export type WizardStepId = 'brief' | 'plan' | 'campaigns' | 'launched';

export interface WizardStepDef {
  id: WizardStepId;
  path: string;
  labelKey: ParseKeys<'nav'>;
  // Backend WizardStep this UI step maps to, used to scope the in-wizard
  // Copilot suggestion chips. Undefined for steps with no editing chips
  // (launched — the strategy is already pushed).
  suggestionStep?: WizardStep;
}

export const WIZARD_STEPS: readonly WizardStepDef[] = [
  {
    id: 'brief',
    path: '/wizard/brief',
    labelKey: 'route.wizard_brief.title',
    suggestionStep: WizardStep.BRIEF,
  },
  {
    id: 'plan',
    path: '/wizard/plan',
    labelKey: 'route.wizard_plan.title',
    suggestionStep: WizardStep.PLAN,
  },
  {
    id: 'campaigns',
    path: '/wizard/campaigns',
    labelKey: 'route.wizard_campaigns.title',
    suggestionStep: WizardStep.CAMPAIGNS,
  },
  {
    id: 'launched',
    path: '/wizard/launched',
    labelKey: 'route.wizard_launched.title',
  },
] as const;

const PINNED_STEPS = WIZARD_STEPS.filter((step) => step.id !== 'plan');

// A pinned plan is built in code, so its draft has no plan step to show or visit.
export const isPinnedDraft = (session: StrategyWizardSessionData): boolean =>
  session.media_plan.pinned === true;

export const wizardStepsFor = (pinned: boolean): readonly WizardStepDef[] =>
  pinned ? PINNED_STEPS : WIZARD_STEPS;

export const deriveCurrentStep = (pathname: string): WizardStepId =>
  WIZARD_STEPS.find((step) => pathname.startsWith(step.path))?.id ?? 'brief';

export const suggestionStepFor = (id: WizardStepId): WizardStep | undefined =>
  WIZARD_STEPS.find((step) => step.id === id)?.suggestionStep;
